/**
 * DomainSetupDialog Component
 *
 * Modal host for the 2-step "add a custom domain" wizard. Since follow-up F1 the
 * wizard body itself lives in `DomainSetupForm` (shared with the inline
 * `CustomDomainSection addMode="inline"` mount used by the launch-activation
 * domain Drawer); this file is only the SDialog chrome:
 *   - title + close behaviour (blocked while a setup request is in flight),
 *   - the form's current step action rendered in the SDialog footer.
 *
 * Pattern reference: src/modules/analytics-settings/components/AddIntegrationDialog.tsx
 */

import React, { useCallback, useState } from 'react';
import { SDialog } from '@modules/ui-components';
import { DomainSetupForm, type DomainSetupFormAction } from './DomainSetupForm';
import type { DomainSetupResponse } from '../types/customDomain.types';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';

/**
 * Component props
 */
export interface DomainSetupDialogProps {
  /** Dialog open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Setup domain handler - returns setup response with CNAME info */
  onSetup: (domain: string) => Promise<DomainSetupResponse>;
  /** Render step 1 copy WITHOUT the workflow guide anchors (threaded from CustomDomainSection, AC-018). */
  suppressGuideAnchors?: boolean;
}

/**
 * Domain Setup Dialog Component
 */
export const DomainSetupDialog: React.FC<DomainSetupDialogProps> = ({
  open,
  onClose,
  onSetup,
  suppressGuideAnchors = false,
}) => {
  const [action, setAction] = useState<DomainSetupFormAction | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title="Add Custom Domain"
      maxWidth="sm"
      fullWidth
      data-testid={CUSTOM_DOMAIN_TEST_IDS.setupDialog}
      primaryAction={action ?? undefined}
    >
      <DomainSetupForm
        onSetup={onSetup}
        onDone={handleClose}
        suppressGuideAnchors={suppressGuideAnchors}
        resetToken={open}
        actionPlacement="host"
        onActionChange={setAction}
        onSubmittingChange={setSubmitting}
      />
    </SDialog>
  );
};

export default DomainSetupDialog;
